import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { haptic } from '../../lib/tma';
import type { HapticKind } from '../../lib/tma';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'tg';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  hapticKind?: HapticKind | false;
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 font-medium ' +
  'rounded-xl select-none ' +
  'transition-[transform,opacity,background-color] duration-150 ease-smooth ' +
  'active:scale-[0.97] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-tg-bg';

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-brand-on hover:bg-brand-hover active:bg-brand-pressed',
  secondary: 'bg-tg-section-bg text-tg-text border border-tg-hint/40 hover:bg-tg-section-bg/80',
  ghost: 'bg-transparent text-tg-text hover:bg-tg-section-bg',
  destructive: 'bg-destructive text-destructive-on hover:opacity-90',
  // 'tg' использует нативный цвет Telegram-кнопки темы (например, для CTA-в-стиле-системы).
  tg: 'bg-tg-button text-tg-button-text hover:opacity-90',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    leftIcon,
    rightIcon,
    hapticKind,
    className,
    children,
    disabled,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  const defaultHaptic: HapticKind =
    variant === 'destructive'
      ? 'warning'
      : variant === 'primary' || variant === 'tg'
        ? 'medium'
        : 'light';
  const hk = hapticKind === undefined ? defaultHaptic : hapticKind;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={(e) => {
        if (isDisabled) return;
        if (hk) haptic(hk);
        onClick?.(e);
      }}
      className={cn(baseClasses, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      )}
    </button>
  );
});

function Spinner() {
  return (
    <span
      className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
      aria-label="Загрузка"
    />
  );
}
